import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';

// Attendance dispute — submission form (docs/modules/workflow-ld-dispute.md).
// "I actually attended this session." Always for the logged-in user; approval
// records the attendance and removes any absence for that session.
const WorkflowDisputeNew = () => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [message, setMessage] = useState('');
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    fetchJSON('/api/ld/dispute-requests/options')
      .then((d) => { if (active) setSessions(d.sessions || []); })
      .catch((e) => { if (active) setOptionsError(e.message); });
    return () => { active = false; };
  }, []);

  const selectedSession = sessions.find(s => String(s.id) === String(sessionId)) || null;

  // Everything still empty, keyed to the control it belongs to (the same
  // `field=` keys the detail page's return comments anchor to).
  const gaps = useMemo(() => {
    const g = [];
    if (!sessionId) g.push({ key: 'sessionTitle', label: 'Training Session' });
    if (!message.trim()) g.push({ key: 'message', label: 'Why the Record Is Wrong' });
    return g;
  }, [sessionId, message]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('sessionId', sessionId);
      form.set('message', message.trim());
      if (remarks.trim()) form.set('remarks', remarks.trim());
      files.forEach(f => form.append('files', f));

      const data = await fetchJSON('/api/ld/dispute-requests', { method: 'POST', body: form });
      navigate(`/workflow/dispute/${data.request.id}`);
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/dispute"
      backLabel="Attendance Dispute"
      title="New Attendance Dispute Declaration"
      subtitle="Dispute a session marked as missed."
      error={error || validation.error}
      onSubmit={submit}
      submitLabel="Submit"
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Session">
          <Field
            label="Training Session"
            required
            field="sessionTitle"
            hint={<>Sessions from the last 90 days. <InfoTip text="If you did miss it, file a training absence instead." /></>}
          >
            <select
              value={sessionId}
              onChange={e => setSessionId(e.target.value)}
              className={inputCls}
            >
              <option value="">Select…</option>
              {sessions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.courseTitle} — {formatDate(s.date)}
                </option>
              ))}
            </select>
            {selectedSession && (
              <p className="mt-1 text-xs text-slate-500">
                {[selectedSession.sessionCode, formatDate(selectedSession.date)].filter(Boolean).join(' · ')}
              </p>
            )}
            {optionsError && <p className="mt-1 text-xs text-red-600">{optionsError}</p>}
            {!optionsError && sessions.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">No sessions in the last 90 days.</p>
            )}
          </Field>
        </FormSection>

        <FormSection step={2} title="What Happened">
          <Field
            label="Why the Record Is Wrong"
            required
            field="message"
            hint="Anything that helps HR confirm it — who you sat with, whether you signed a sheet, why the QR check-in did not go through."
          >
            <textarea rows="4" value={message} onChange={e => setMessage(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={3} title="Attachments">
          <Field
            label="Upload Evidence"
            hint="A photo of the sign-in sheet, an email from the trainer. PDF / JPG / PNG / DOC, up to 30 MB per file, 5 files at a time."
          >
            <FilePicker
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              disabled={saving}
              onChange={e => setFiles(Array.from(e.target.files || []))}
              files={files}
            />
          </Field>
        </FormSection>

        <FormSection step={4} title="Remarks">
          <Field label="Remarks">
            <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowDisputeNew;
